// @flow
import { createAction } from 'redux-actions';
import get from 'lodash/get';

import userAuth from 'utils/user-auth';

export const getPayerAuthenticatedRequest = createAction<'GET_PAYER_AUTHENTICATED_REQUEST', any>(
  'GET_PAYER_AUTHENTICATED_REQUEST',
);
export const getPayerAuthenticatedSuccess = createAction<'GET_PAYER_AUTHENTICATED_SUCCESS', any>(
  'GET_PAYER_AUTHENTICATED_SUCCESS',
);
export const getPayerAuthenticatedFailure = createAction<'GET_PAYER_AUTHENTICATED_FAILURE', any>(
  'GET_PAYER_AUTHENTICATED_FAILURE',
);

export const getNewPayerAuthenticatedRequest = createAction<
  'GET_NEW_PAYER_AUTHENTICATED_REQUEST',
  any,
>('GET_NEW_PAYER_AUTHENTICATED_REQUEST');
export const getNewPayerAuthenticatedSuccess = createAction<
  'GET_NEW_PAYER_AUTHENTICATED_SUCCESS',
  any,
>('GET_NEW_PAYER_AUTHENTICATED_SUCCESS');
export const getNewPayerAuthenticatedFailure = createAction<
  'GET_NEW_PAYER_AUTHENTICATED_FAILURE',
  any,
>('GET_NEW_PAYER_AUTHENTICATED_FAILURE');

export const postPayerPaymentMethodRequest = createAction<'POST_PAYER_PAYMENT_METHOD_REQUEST', any>(
  'POST_PAYER_PAYMENT_METHOD_REQUEST',
);
export const postPayerPaymentMethodSuccess = createAction<'POST_PAYER_PAYMENT_METHOD_SUCCESS', any>(
  'POST_PAYER_PAYMENT_METHOD_SUCCESS',
);
export const postPayerPaymentMethodFailure = createAction<'POST_PAYER_PAYMENT_METHOD_FAILURE', any>(
  'POST_PAYER_PAYMENT_METHOD_FAILURE',
);

export const postNewPayerPaymentMethodRequest = createAction<
  'POST_NEW_PAYER_PAYMENT_METHOD_REQUEST',
  any,
>('POST_NEW_PAYER_PAYMENT_METHOD_REQUEST');
export const postNewPayerPaymentMethodSuccess = createAction<
  'POST_NEW_PAYER_PAYMENT_METHOD_SUCCESS',
  any,
>('POST_NEW_PAYER_PAYMENT_METHOD_SUCCESS');
export const postNewPayerPaymentMethodFailure = createAction<
  'POST_NEW_PAYER_PAYMENT_METHOD_FAILURE',
  any,
>('POST_NEW_PAYER_PAYMENT_METHOD_FAILURE');

export const postPayerChargeConfirmationRequest = createAction<
  'POST_PAYER_CHARGE_CONFIRMATION_REQUEST',
  any,
>('POST_PAYER_CHARGE_CONFIRMATION_REQUEST');
export const postPayerChargeConfirmationSuccess = createAction<
  'POST_PAYER_CHARGE_CONFIRMATION_SUCCESS',
  any,
>('POST_PAYER_CHARGE_CONFIRMATION_SUCCESS');
export const postPayerChargeConfirmationFailure = createAction<
  'POST_PAYER_CHARGE_CONFIRMATION_FAILURE',
  any,
>('POST_PAYER_CHARGE_CONFIRMATION_FAILURE');

export const getPayerRequest = createAction<'GET_PAYER_REQUEST', any>('GET_PAYER_REQUEST');
export const getPayerSuccess = createAction<'GET_PAYER_SUCCESS', any>('GET_PAYER_SUCCESS');
export const getPayerFailure = createAction<'GET_PAYER_FAILURE', any>('GET_PAYER_FAILURE');

export const incrementPayerPage = createAction<'INCREMENT_PAYER_PAGE', any>('INCREMENT_PAYER_PAGE');
export const toggleCreditCardForm = createAction<'TOGGLE_CREDIT_CARD_FORM', any>(
  'TOGGLE_CREDIT_CARD_FORM',
);

const setUserAuth = response => {
  const { payer, businesses = {} } = response;
  const [{ id }] = (Object.values(payer): any);
  const [user]: any = Object.values(payer);
  const businessId = Object.keys(businesses)[0];
  const business = businesses[businessId];
  const logoUrl = get(business, 'attributes.logoUrl');
  const businessName = get(business, 'attributes.name');
  const clientTermsUrl = get(business, 'attributes.clientTermsUrl');
  const forceAutopay = get(business, 'attributes.forceAutopay');

  const payerAuthJson = {
    type: 'payer',
    id,
    business: {
      id: businessId,
      name: businessName,
      logoUrl,
      clientTermsUrl,
      forceAutopay,
    },
    attributes: user.attributes,
  };

  userAuth.set(JSON.stringify(payerAuthJson));
};

const getPayer = () => async (dispatch, getState, api) => {
  dispatch(getPayerRequest());
  try {
    const response = await api.getPayer();
    api.checkStatus(response);
    dispatch(getPayerSuccess(response));
  } catch (error) {
    dispatch(getPayerFailure(error));
  }
};

export const getPayerAuthenticated = (token: any) => async (
  dispatch: any,
  getState: any,
  api: any,
) => {
  dispatch(getPayerAuthenticatedRequest());
  try {
    const response = await api.getPayerAuthenticated(token);
    api.checkStatus(response);

    setUserAuth(response);

    dispatch(getPayerAuthenticatedSuccess(response));
  } catch (error) {
    dispatch(getPayerAuthenticatedFailure(error));
  }
};

export const getNewPayerAuthenticated = (token: any) => async (
  dispatch: any,
  getState: any,
  api: any,
) => {
  dispatch(getNewPayerAuthenticatedRequest());
  try {
    const response = await api.getNewPayerAuthenticated(token);
    api.checkStatus(response);

    setUserAuth(response);

    dispatch(getNewPayerAuthenticatedSuccess(response));
  } catch (error) {
    dispatch(getNewPayerAuthenticatedFailure(error));
  }
};

export const postPayerPaymentMethod = (data: any) => async (
  dispatch: any,
  getState: any,
  api: any,
) => {
  dispatch(postPayerPaymentMethodRequest());
  try {
    const {
      token = {},
      bankAccountPublicToken,
      bankAccountId,
      bankAccountType,
      email,
      chargeId,
      autopayEnabled,
    } = data;
    const { id: stripeCardToken } = token;

    const postData = {
      data: {
        attributes: {
          autopay_enabled: autopayEnabled,
          stripe_card_token: stripeCardToken,
          bank_account_token: bankAccountPublicToken,
          bank_account_id: bankAccountId,
          bank_account_type: bankAccountType,
          email,
          charge_id: chargeId,
        },
      },
    };

    const response = await api.postPayerPaymentMethod(postData);
    api.checkStatus(response);
    dispatch(postPayerPaymentMethodSuccess(response));
    dispatch(getPayer());
    dispatch(incrementPayerPage());
  } catch (error) {
    dispatch(postPayerPaymentMethodFailure(error));
  }
};

export const postNewPayerPaymentMethod = (data: any) => async (
  dispatch: any,
  getState: any,
  api: any,
) => {
  dispatch(postNewPayerPaymentMethodRequest());
  try {
    const {
      token = {},
      bankAccountPublicToken,
      bankAccountId,
      bankAccountType,
      email,
      autopayEnabled,
    } = data;
    const { id: stripeCardToken } = token;

    const postData = {
      data: {
        attributes: {
          autopay_enabled: autopayEnabled,
          stripe_card_token: stripeCardToken,
          bank_account_token: bankAccountPublicToken,
          bank_account_id: bankAccountId,
          bank_account_type: bankAccountType,
          email,
        },
      },
    };

    const response = await api.postPayerPaymentMethod(postData);
    api.checkStatus(response);
    dispatch(postNewPayerPaymentMethodSuccess(response));
    dispatch(getPayer());
    dispatch(incrementPayerPage());
  } catch (error) {
    dispatch(postNewPayerPaymentMethodFailure(error));
  }
};

export const postPayerChargeConfirmation = (data: any) => async (
  dispatch: any,
  getState: any,
  api: any,
) => {
  dispatch(postPayerChargeConfirmationRequest());
  const { chargeId } = data;
  try {
    const response = await api.postPayerChargeConfirmation(chargeId);
    api.checkStatus(response);
    dispatch(postPayerChargeConfirmationSuccess(response));
    dispatch(getPayer());
    dispatch(incrementPayerPage());
  } catch (error) {
    dispatch(postPayerChargeConfirmationFailure(error));
  }
};



// WEBPACK FOOTER //
// ./src/actions/payer/index.js